import RegistrationAnalysisSection = Infospeed.Services.ClassWeb.API.Contracts.COT.RegistrationAnalysisSection;
import RegistrationAnalysis = Infospeed.Services.ClassWeb.API.Contracts.COT.RegistrationAnalysis;
import WebBookingControlType = Infospeed.Services.ClassWeb.API.Contracts.COT.WebBookingControlType;
import AnalysisUDFItem = Infospeed.Services.ClassWeb.API.Contracts.COT.AnalysisUDFItem;

import { Component, OnInit, Input, OnChanges, SimpleChange, DoCheck, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { CotFormControlComponent, ICotControlChangeEvent } from '../cot-form-control/cot-form-control.component';
import { ControlContainer, NgForm } from '@angular/forms';

@Component({
    selector: 'cot-analysis-form',
    templateUrl: './cot-analysis-form.component.html',
    styleUrls: ['../widgets-custom-theme.scss', './cot-analysis-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class CotAnalysisFormComponent implements OnInit, OnChanges {
    private readonly validCheckedValues: string[] = ['true', 't', 'yes', 'y', '1'];
    private readonly validUncheckedvalues: string[] = ['false', 't', 'no', 'n', '0'];
    // inputs
    @Input('analyses') analyses: RegistrationAnalysis[] = [];
    @Input('columns') columns: number = 1;
    @Input('form') form: NgForm;
    @Input('standalone') standalone: boolean = false;
    @Input('align') align: string = 'left';
    @Input('disabled') disabled: boolean = false;
    @Input('touchedActivatedError') touchedActivatedError: boolean = false;
    // private properties
    private cotAnalyses: ICotAnalysis[] = [];
    // public properties
    singleDimCotAnalyses: ICotAnalysis[] = [];
    multiDimCotAnalyses: ICotAnalysis[][] = [];
    // output
    @Output('onChange') onChange: EventEmitter<ICotAnalysisChangeEvent> = new EventEmitter<ICotAnalysisChangeEvent>();

    constructor(private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.cotAnalyses = this.convertToCotAnalyses(this.analyses);
        if (this.columns == 1) {
            this.singleDimCotAnalyses = this.cotAnalyses;
        }
        else if (this.columns > 1) {
            this.multiDimCotAnalyses = this.generateMultiDimCotAnalyses(this.cotAnalyses, this.columns);
        }
        else { }
        // set default values
        this.setDefaultValues();
    }

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) { // model reference changes
        for (let propName in changes) {
            let changedProp = changes[propName];
            if (propName === 'analyses') {
                this.cotAnalyses = this.convertToCotAnalyses(this.analyses);
            }
        }
    }

    ngDoCheck() { // property of the model changes
    }

    onControlChange($event: ICotControlChangeEvent) {
        // update the actual registration-analysis object
        this.setAnalysisValue($event);
    }

    public getInvalidControls(): any {
        const invalidControl = [];
        for (let i: number = 0; i < this.cotAnalyses.length; i++) {
            if (this.form.controls[this.cotAnalyses[i].AnalysisNameId])
                if (this.form.controls[this.cotAnalyses[i].AnalysisNameId].invalid) {
                    invalidControl.push(this.form.controls[this.cotAnalyses[i].AnalysisNameId])
                }
        }
        return invalidControl;
    }

    public resetToDefault() {
        this.cotAnalyses.forEach(x => {
            const control = this.form.control.get(x.AnalysisNameId.toString());
            if (control)
                control.reset();
            switch (x.WebBookingControlType) {
                case WebBookingControlType.CheckBox:
                case WebBookingControlType.RadioButton:
                case WebBookingControlType.RadioButtonSimple:
                    if (control) {
                        control.setValue(false);
                        this.setAnalysisValue(<ICotControlChangeEvent>{ Id: x.AnalysisNameId, Type: x.WebBookingControlType, Value: x.Value, AdditionalInfo: null })
                        //control.updateValueAndValidity({ onlySelf: true });
                    }
                    break;
            }
        });
    }

    public disable(disable: boolean = true) {
        this.cotAnalyses.forEach(x => {
            const control = this.form.control.get(x.AnalysisNameId.toString());
            if (control)
                if (disable)
                    control.disable({ onlySelf: true });
                else
                    control.enable({ onlySelf: true });
        });
    }

    private convertToCotAnalyses(analyses: RegistrationAnalysis[]): ICotAnalysis[] {
        let cotAnalyses: ICotAnalysis[] = [];
        if (analyses) {
            analyses.forEach(x => cotAnalyses.push(this.convertToCotAnalysis(x)));
        }
        return cotAnalyses;
    }

    private convertToCotAnalysis(analysis: RegistrationAnalysis): ICotAnalysis {
        let cotAnalysis: ICotAnalysis = {
            AnalysisName: analysis.AnalysisName,
            AnalysisNameId: analysis.AnalysisNameId,
            AnalysisSectionId: analysis.AnalysisSectionId,
            Content: analysis.Content,
            Date: analysis.Date,
            DefaultNoteText: analysis.DefaultNoteText,
            IsMandatory: analysis.IsMandatory,
            IsPickOnly: analysis.IsPickOnly,
            Note: analysis.Note,
            PickItems: analysis.PickItems,
            Sequence: analysis.Sequence,
            Value: null,
            WebBookingControlType: analysis.WebBookingControlType,
            WebBookingRegisterType: analysis.WebBookingRegisterType,
        };
        return cotAnalysis;
    }

    private setDefaultValues() {
        this.cotAnalyses.forEach(x => {
            this.setDefaultValue(x);
        });
    }

    private setDefaultValue(cotAnalysis: ICotAnalysis) {
        switch (cotAnalysis.WebBookingControlType) {
            case WebBookingControlType.CheckBox:
                cotAnalysis.Value = false;
                this.setAnalysisValue(<ICotControlChangeEvent>{ Id: cotAnalysis.AnalysisNameId, Type: cotAnalysis.WebBookingControlType, Value: cotAnalysis.Value, AdditionalInfo: null })
                break;
            case WebBookingControlType.Date:
            case WebBookingControlType.DateTime:
            case WebBookingControlType.Dropdown:
            case WebBookingControlType.Email:
            case WebBookingControlType.PhoneNumber:
            case WebBookingControlType.Money:
            case WebBookingControlType.Number:
                break;
            case WebBookingControlType.RadioButton:
            case WebBookingControlType.RadioButtonSimple:
                cotAnalysis.Value = false;
                this.setAnalysisValue(<ICotControlChangeEvent>{ Id: cotAnalysis.AnalysisNameId, Type: cotAnalysis.WebBookingControlType, Value: cotAnalysis.Value, AdditionalInfo: "" })
                break;
            case WebBookingControlType.Text:
            case WebBookingControlType.TextArea:
            case WebBookingControlType.Time:
            default:
                break;
        }
    }

    private generateMultiDimCotAnalyses(cotAnalyses: ICotAnalysis[], columns: number = 2): ICotAnalysis[][] {
        let noOfColumns: number = columns;
        //let noOfRows: number = Math.ceil(cotAnalyses.length / noOfColumns);
        let arrangedAnalyses: ICotAnalysis[][] = [];
        let arrangedColumn: ICotAnalysis[] = [];
        let analyses: ICotAnalysis[] = cotAnalyses;
        let counter: number = 0;
        let reviewedNoOfRows: number = 0;
        for (var i = 0; i < noOfColumns; i++) {
            arrangedColumn = [];
            reviewedNoOfRows = Math.ceil((cotAnalyses.length - counter) / (noOfColumns - i));
            for (var j = 0; j < reviewedNoOfRows; j++) {
                if (counter < analyses.length) {
                    arrangedColumn.push(analyses[counter]);
                    counter++;
                }
            }
            arrangedAnalyses.push(arrangedColumn);
        }
        return arrangedAnalyses;
    }

    private setAnalysisValue(event: ICotControlChangeEvent): void {
        let cotAnalysis: ICotAnalysis = this.cotAnalyses.filter(x => x.AnalysisNameId == event.Id)[0];
        switch (event.Type) {
            case WebBookingControlType.CheckBox:
                cotAnalysis.Content = this.getPickItemContentForChecked(event.Value, cotAnalysis.PickItems);
                break;
            case WebBookingControlType.Date:
                cotAnalysis.Date = event.Value;
                break;
            case WebBookingControlType.DateTime: // note: future release: set as Date if material support datetime control
                //cotAnalysis.Date = event.Value;
                cotAnalysis.Content = event.Value;
                break;
            case WebBookingControlType.Dropdown:
                cotAnalysis.Content = event.Value;
                break;
            case WebBookingControlType.Email:
                cotAnalysis.Note = event.Value;
                break;
            case WebBookingControlType.PhoneNumber:
                cotAnalysis.Content = event.Value;
                break;
            case WebBookingControlType.Money:
                cotAnalysis.Content = event.Value;
                break;
            case WebBookingControlType.Number:
                cotAnalysis.Content = event.Value;
                break;
            case WebBookingControlType.RadioButton:
                cotAnalysis.Content = this.getPickItemContentForChecked(event.Value, cotAnalysis.PickItems);
                cotAnalysis.Note = event.AdditionalInfo;
                break;
            case WebBookingControlType.RadioButtonSimple:
                cotAnalysis.Content = this.getPickItemContentForChecked(event.Value, cotAnalysis.PickItems);
                break;
            case WebBookingControlType.Text:
                cotAnalysis.Note = event.Value;
                break;
            case WebBookingControlType.TextArea:
                cotAnalysis.Note = event.Value;
                break;
            case WebBookingControlType.Time:
                cotAnalysis.Content = event.Value;
                break;
            default:
                cotAnalysis.Content = event.Value;
                break;
        }

        if (this.standalone)
            this.updateRegistrationAnalysisSection(cotAnalysis);

        // emmit change event
        this.onChange.emit({ CotAnalysis: cotAnalysis });
    }

    private getPickItemContentForChecked(isChecked: boolean, pickItems: string[]): string {
        let content: string;
        if (isChecked) {
            if (pickItems.length > 0) {
                var validCheckedValue = pickItems.filter(x => this.validCheckedValues.indexOf(x.toLocaleLowerCase()) != -1);
                if (validCheckedValue && validCheckedValue.length > 0)
                    content = validCheckedValue[0];
                else
                    content = 'Yes';
            }
            else
                content = 'Yes';
        }
        else {
            if (pickItems.length > 0) {
                var validCheckedValue = pickItems.filter(x => this.validUncheckedvalues.indexOf(x.toLocaleLowerCase()) != -1);
                if (validCheckedValue && validCheckedValue.length > 0)
                    content = validCheckedValue[0];
                else
                    content = 'No';
            }
            else
                content = 'No';
        }
        return content;
    }

    public reset() {
        this.analyses.forEach(x => {
            const control = this.form.control.get(x.AnalysisNameId.toString());
            if (control)
                control.reset();
        });
    }

    public getCheckedForContent(content: string): boolean {
        if (this.validCheckedValues.indexOf(content.toLocaleLowerCase()) != -1)
            return true;
        else
            return false;
    }

    // standalone methods: start
    private updateRegistrationAnalysisSection(cotAnalysis: ICotAnalysis) {
        let registrationAnalysis: RegistrationAnalysis = this.analyses.filter(x => x.AnalysisNameId == cotAnalysis.AnalysisNameId)[0];
        registrationAnalysis.Content = cotAnalysis.Content;
        registrationAnalysis.Date = cotAnalysis.Date;
        registrationAnalysis.Note = cotAnalysis.Note;
    }

    public getIAnalysisUDFItems(): any[] {
        let iAnalysisUDFItems: any[] = [];
        iAnalysisUDFItems = ConvertToIAnalysisUDFItems(this.analyses);
        return iAnalysisUDFItems;
    }

    public validateControls() {
        this.analyses.forEach(x => {
            const control = this.form.controls[x.AnalysisNameId];
            if (control && control.invalid) {
                control.markAsTouched({ onlySelf: true });
                control.updateValueAndValidity({ onlySelf: true }); // updateValueAndValidity() is required to light the invalid control
            }
            if (x.WebBookingControlType === WebBookingControlType.RadioButton) {
                const controlAdditionalInfo = this.form.controls[x.AnalysisNameId + "AdditionalInfo"];
                if (controlAdditionalInfo && controlAdditionalInfo.invalid) {
                    controlAdditionalInfo.markAsTouched({ onlySelf: true });
                    controlAdditionalInfo.updateValueAndValidity({ onlySelf: true }); // updateValueAndValidity() is required to light the invalid control
                }
            }
        });
    }

    public setValueAndUpdateValidity(analysis: RegistrationAnalysis, value: any, additionalInfo?: any) {
        const changeEvent: ICotControlChangeEvent = <ICotControlChangeEvent>{ Id: analysis.AnalysisNameId, Type: analysis.WebBookingControlType, Value: value, AdditionalInfo: additionalInfo };
        this.setAnalysisValue(changeEvent);
        const control = this.form.controls[analysis.AnalysisNameId];
        if (control) {
            control.setValue(changeEvent.Value);
            control.updateValueAndValidity({ onlySelf: true });
        }
    }

    public isValid(): boolean {
        return this.analyses.some(x => {
            const control = this.form.controls[x.AnalysisNameId];
            if (control && control.invalid) {
                return true;
            }
        });
    }
    // standalone methods: end
}

export interface ICotAnalysis extends RegistrationAnalysis {
    Value: any;
}

export interface ICotAnalysisChangeEvent {
    CotAnalysis: ICotAnalysis;
}

function ConvertToAnalysisUDFItems(analyses: RegistrationAnalysis[]): AnalysisUDFItem[] {
    let udfItems: AnalysisUDFItem[] = [];
    analyses.forEach(item => {
        let udfItem: AnalysisUDFItem = {
            AnalysisUDFItemId: 0,
            AnalysisUDFNameId: item.AnalysisNameId,
            AnalysisUDFSectionId: item.AnalysisSectionId,
            Content: item.Content,
            Date: item.Date,
            ShortNote: item.Note,
        };
        udfItems.push(udfItem);
    });
    return udfItems;
}

export function ConvertToIAnalysisUDFItems(analyses: RegistrationAnalysis[]): any[] {
    let iAnalysisUDFItems: any[] = [];
    ConvertToAnalysisUDFItems(analyses).forEach(x => {
        let iAnalysisUDFItem: any = {
            AnalysisUDFItemId: 0,
            AnalysisUDFSectionId: x.AnalysisUDFSectionId,
            AnalysisUDFNameId: x.AnalysisUDFNameId,
            AnalysisUDFName: "",
            Date: x.Date,
            Content: x.Content,
            ShortNote: x.ShortNote,
        };
        iAnalysisUDFItems.push(iAnalysisUDFItem);
    });
    return iAnalysisUDFItems;
}



// WEBPACK FOOTER //
// ./src/app/widgets/cot-analysis-form/cot-analysis-form.component.ts